import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import Monogram from "../../Assets/Monogram.png";
import { useLoadingStore } from "../../lib/stores/stores";
import { usePostHog } from 'posthog-js/react';

// shadcn/ui components
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { cn } from "@/lib/utils";

// Icons
import { MdOutlineDesignServices } from "react-icons/md";
import { GiWingfoot } from "react-icons/gi";

// --- Constant Definitions ---
const NAV_MAPPING = {
    '/Projects': 0,
    '/': 1, // Home
    '/Chat': 2, // Hermes
    '/Delos': 0,
    '/Midas': 0,
    '/Orwell': 0,
    '/DS': 0,
    '/DA': 0,
    '/Prometheus': 0,
    '/CovidPredictor': 0,
    '/SensorData': 0,
    '/PrivacyPolicy': null,
};

const BOTTOM_NAV_ROUTES = ["/Projects", "/", "/Chat"];

// --- Simplified Home Icon Component (inherits color) ---
const HomeIcon = () => (
    <svg
        version="1.0" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
        viewBox="0 0 158.000000 155.000000" preserveAspectRatio="xMidYMid meet"
        className="fill-current"
    >
        <g transform="translate(0.000000,155.000000) scale(0.100000,-0.100000)" stroke="none">
            <path d="M450 1270 l0 -280 -225 0 -225 0 0 -90 0 -90 225 0 225 0 0 -405 0 -405 565 0 565 0 0 85 0 85 -460 0 -460 0 0 320 0 320 253 2 252 3 3 88 3 87 -256 0 -255 0 0 195 0 195 445 0 445 0 0 85 0 85 -550 0 -550 0 0 -280z"/>
        </g>
    </svg>
);


function NavBar() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);

    const location = useLocation();
    const posthog = usePostHog();
    const navigate = useNavigate();
    const isKeyboardOpen = useDetectKeyboardOpen();
    const isLoading = useLoadingStore((state) => state.loading);

    const [value, setValue] = useState(() => {
        const initialTargetKey = '/' + (location.pathname.split('/')[1] || '');
        const initialValue = NAV_MAPPING[initialTargetKey];
        return (initialValue !== undefined && initialValue !== null && initialValue >= 0) ? initialValue : 1;
    });

    const scrollHandler = useCallback(() => {
        setIsScrolled(window.scrollY >= 20);
    }, []);

    const handleWindowSizeChange = useCallback(() => {
        setWidth(window.innerWidth);
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", scrollHandler);
        return () => window.removeEventListener("scroll", scrollHandler);
    }, [scrollHandler]);

    useEffect(() => {
        window.addEventListener("resize", handleWindowSizeChange);
        return () => window.removeEventListener("resize", handleWindowSizeChange);
    }, [handleWindowSizeChange]);

    useEffect(() => {
        const pathSegments = location.pathname.split('/');
        const targetKey = '/' + (pathSegments[1] || '');
        const navValue = NAV_MAPPING[targetKey];

        if (navValue !== undefined && navValue !== null && navValue >= 0) {
            setValue(navValue);
        } else {
             setValue(null);
        }

        if (posthog) {
            posthog.capture('$pageview');
        }
    }, [location, posthog]);


    function handleBottomNavChange(newValue) {
        if (BOTTOM_NAV_ROUTES[newValue]) {
            navigate(BOTTOM_NAV_ROUTES[newValue]);
        }
    }

    const isMobile = width <= 768;

    const desktopNavLinks = (
        <NavigationMenu className="hidden md:flex ml-auto">
            <NavigationMenuList>
                <NavigationMenuItem>
                    <Link to="/" legacyBehavior passHref>
                        <NavigationMenuLink
                            className={cn(
                                navigationMenuTriggerStyle(),
                                'transition-transform duration-200 ease-in-out',
                                value === 1
                                    ? 'font-semibold scale-105 text-foreground'
                                    // MODIFIED: Added hover:scale-105 for inactive state
                                    : 'text-muted-foreground hover:text-foreground hover:scale-105'
                            )}
                        >
                            Home
                        </NavigationMenuLink>
                    </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                    <Link to="/Projects" legacyBehavior passHref>
                        <NavigationMenuLink
                             className={cn(
                                navigationMenuTriggerStyle(),
                                'transition-transform duration-200 ease-in-out',
                                value === 0
                                    ? 'font-semibold scale-105 text-foreground'
                                    // MODIFIED: Added hover:scale-105 for inactive state
                                    : 'text-muted-foreground hover:text-foreground hover:scale-105'
                            )}
                        >
                            Projects
                        </NavigationMenuLink>
                    </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                    <Link to="/Chat" legacyBehavior passHref>
                        <NavigationMenuLink
                             className={cn(
                                navigationMenuTriggerStyle(),
                                'transition-transform duration-200 ease-in-out',
                                value === 2
                                    ? 'font-semibold scale-105 text-foreground'
                                    // MODIFIED: Added hover:scale-105 for inactive state
                                    : 'text-muted-foreground hover:text-foreground hover:scale-105'
                            )}
                        >
                            Hermes
                        </NavigationMenuLink>
                    </Link>
                </NavigationMenuItem>
            </NavigationMenuList>
        </NavigationMenu>
    );

    const bottomNavItems = [
        { label: "Projects", icon: <MdOutlineDesignServices size={22} />, index: 0 },
        { label: "Home", icon: <HomeIcon />, index: 1 },
        { label: "Hermes", icon: <GiWingfoot size={22} />, index: 2 },
    ];


    return (
        <>
            {/* --- Top Navigation Header --- */}
            {!isMobile && (
            <header
                className={cn(
                    "fixed top-0 left-0 right-0 transition-all duration-300 ease-in-out",
                    "z-[110]",
                    (isScrolled)
                        ? "border-b bg-background/95 backdrop-blur-sm shadow-sm"
                        : "bg-transparent border-b border-transparent",
                )}
            >
                <div className="container mx-auto flex h-16 items-center justify-between px-4">
                    <Link to="/" className="flex items-center">
                        <img
                            src={Monogram}
                            alt="EK Logo"
                            className="h-8 w-8"
                        />
                    </Link>
                    {desktopNavLinks}
                </div>

                {isLoading &&
                    <LinearProgress
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left:0,
                            width: '100%',
                            height: '3px', 
                            "& .MuiLinearProgress-bar": {
                                backgroundImage: 'linear-gradient(to right, black, black)',
                            },
                            backgroundColor: 'transparent',
                        }}
                    />
                }
            </header>
            )}

            {/* --- Bottom Navigation (Mobile Only) --- */}
            {isMobile && !isKeyboardOpen && (
                <div className="fixed inset-x-0 bottom-0 z-[110] md:hidden">
                    {isLoading &&
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '3px', 
                                "& .MuiLinearProgress-bar": {
                                   backgroundImage: 'linear-gradient(to right, black, black)',
                                },
                                backgroundColor: 'transparent',
                            }}
                            />
                        </Box>
                    }
                    <nav className="flex h-16 items-center justify-around border-t bg-white shadow-inner dark:bg-gray-950">
                        {bottomNavItems.map((item) => (
                             <Button
                                key={item.index}
                                variant="ghost"
                                className={cn(
                                    "group flex h-full flex-col items-center justify-center gap-1 px-2 pt-1 text-xs w-[33.33%]",
                                    "transition-transform duration-200 ease-in-out",
                                    value === item.index
                                        ? 'font-semibold scale-110 text-foreground'
                                        : 'text-muted-foreground hover:text-foreground'
                                )}
                                onClick={() => handleBottomNavChange(item.index)}
                            >
                                {item.icon}
                                <span className="mt-0.5">{item.label}</span>
                            </Button>
                        ))}
                    </nav>
                </div>
            )}
        </>
    );
}

export default NavBar;